import { createContext, useContext, useMemo, useCallback, useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError, getToken } from "@/lib/api";
import { useLocalStorage } from "@/lib/useLocalStorage";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

export function AppProvider({ children }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeOrderId, setActiveOrderId] = useLocalStorage("trbolt_active_order", null);
  const [recents, setRecents] = useLocalStorage("trbolt_recents", []);

  const fetchOrders = useCallback(async () => {
    if (!getToken()) {
      setOrders([]);
      setLoading(false);
      return [];
    }
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
      return data;
    } catch (e) {
      toast.error(apiError(e, "Could not load orders"));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const activeOrder = useMemo(
    () => orders.find((o) => o.id === activeOrderId) || null,
    [orders, activeOrderId]
  );

  const createOrder = useCallback(
    async (name) => {
      try {
        const { data } = await api.post("/orders", { name: name || `Order ${orders.length + 1}` });
        setOrders((prev) => [...prev, data]);
        setActiveOrderId(data.id);
        return data;
      } catch (e) {
        toast.error(apiError(e, "Could not create order"));
        return null;
      }
    },
    [orders.length, setActiveOrderId]
  );

  const deleteOrder = useCallback(
    async (id) => {
      try {
        await api.delete(`/orders/${id}`);
        setOrders((prev) => {
          const next = prev.filter((o) => o.id !== id);
          setActiveOrderId((cur) => (cur === id ? next[0]?.id || null : cur));
          return next;
        });
      } catch (e) {
        toast.error(apiError(e, "Could not delete order"));
      }
    },
    [setActiveOrderId]
  );

  const renameOrder = useCallback(async (id, name) => {
    try {
      const { data } = await api.patch(`/orders/${id}`, { name });
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
    } catch (e) {
      toast.error(apiError(e, "Could not rename order"));
    }
  }, []);

  const addLineToOrder = useCallback(
    async (orderId, line) => {
      try {
        const { data } = await api.post(`/orders/${orderId}/lines`, { line });
        setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
        setActiveOrderId(orderId);
        return data;
      } catch (e) {
        toast.error(apiError(e, "Could not add to order"));
        return null;
      }
    },
    [setActiveOrderId]
  );

  const deleteLine = useCallback(async (orderId, lineId) => {
    try {
      const { data } = await api.delete(`/orders/${orderId}/lines/${lineId}`);
      setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
    } catch (e) {
      toast.error(apiError(e, "Could not delete line"));
    }
  }, []);

  const addRecent = useCallback(
    (rec) => setRecents([{ ...rec, id: uid(), ts: Date.now() }, ...recents].slice(0, 20)),
    [recents, setRecents]
  );
  const clearRecents = useCallback(() => setRecents([]), [setRecents]);

  const value = {
    orders,
    loading,
    activeOrder,
    activeOrderId,
    setActiveOrderId,
    createOrder,
    deleteOrder,
    renameOrder,
    addLineToOrder,
    deleteLine,
    recents,
    addRecent,
    clearRecents,
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
